import { Alert, Divider, Typography } from 'antd';
import { Form } from '@/components';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { useDictaphone } from '@/components/Dictaphone';
import { useSelectableSuboption } from './useSelectableSuboption';
import { ItemListDetailsLocal } from './ItemListDetailsLocal';
import { ClippoAction } from '@webinex/clippo';
import { useRoomLookupSource } from '@/views/Rooms.Common/useRoomLookupSource';

export const schema = Yup.object().shape({
  __hasSuboptions: Yup.bool(),
  itemId: Yup.string().nullable().required({ key: 'item.required' }),
  suboptionId: Yup.string()
    .nullable()
    .when('__hasSuboptions', {
      is: true,
      then: (schema) => schema.required({ key: 'suboption.required' }),
    }),
  notes: Yup.string().field('lg').nullable(),
  floorplanRoomId: Yup.string().nullable(),
});

export interface PersonalizationItemFormValue {
  id: string;
  itemId: string;
  suboptionId: string | undefined;
  notes: string | undefined;
  floorplanRoomId: string | undefined | null;
  attachments: ClippoAction[];
}

export interface AddPersonalizationItemFormValue
  extends PersonalizationItemFormValue {
  __hasSuboptions: boolean;
}

export interface PersonalizationItemFormContentProps {
  homeId: string;
  projectId: string;
  initialValue: PersonalizationItemFormValue;
  isFloorplan: boolean;
}

export function PersonalizationItemFormContent(
  props: PersonalizationItemFormContentProps,
) {
  const { homeId, projectId, initialValue, isFloorplan } = props;
  const { t } = useTranslation();
  const formik = useFormikContext<AddPersonalizationItemFormValue>();
  const { itemId } = formik.values;

  const selectedSubOptionIds = useMemo(
    () => (initialValue.suboptionId ? [initialValue.suboptionId] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const selectableSuboption = useSelectableSuboption(
    selectedSubOptionIds,
    itemId,
  );
  const dictaphoneProps = useDictaphone();

  const roomDataSource = useRoomLookupSource({
    homeId: homeId,
    withWholeHome: true,
  });

  return (
    <>
      <ItemListDetailsLocal
        id={itemId}
        projectId={projectId}
        homeId={homeId}
        selectableSuboption={selectableSuboption}
        includeUnlistedSuboptions={selectedSubOptionIds}
        readonly
      />

      <Divider />

      {isFloorplan && (
        <>
          <Typography.Title level={4} className="pb-2">
            {t('personalization.item.details.floorplanRoomId')}
          </Typography.Title>
          <Form.AsyncSelect
            name="floorplanRoomId"
            dataSource={roomDataSource}
            label={false}
            preload
          />
        </>
      )}

      <Typography.Title level={4} className="pb-2">
        {t('personalization.item.details.notes')}
      </Typography.Title>
      <Form.TextArea
        name="notes"
        label={false}
        enableVoice
        {...dictaphoneProps}
      />
      <Form.DeferAttach
        name="attachments"
        noPreload={!initialValue.id}
        ownerId={initialValue.id}
        ownerType="PersonalizationAttachments"
        showActions
        openOnClick
      />

      <Form.ErrorMessage name="itemId">
        {(error) => <Alert className="mt-3" type="error" description={error} />}
      </Form.ErrorMessage>
      <Form.ErrorMessage name="suboptionId">
        {(error) => <Alert className="mt-3" type="error" description={error} />}
      </Form.ErrorMessage>
    </>
  );
}
